import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Users, Link2, Loader2 } from 'lucide-react';
import { PublishLayout } from '../components/PublishLayout';
import { InternalDetailsForm } from '../components/InternalDetailsForm';
import { Button } from "../components/ui/button";
import { useAuth } from '../contexts/AuthContext';
import { fetchShow, fetchShowMetadata, updateShowMetadata } from '../services/showService';
import { outsourcedShowService } from '../services/outsourcedShowService';
import { ShowMetaDataType, Show } from '../types/Show';
import { OutsourcedShow } from '../types/OutsourcedShow';
import { ShareModal } from '../components/ShareModal';
import { toast } from 'sonner';
import styles from '../styles/Publish.module.css';

interface InternalDetailsData {
  actorEthnicity: string;
  sourceSubtitleLanguage: string;
  trailerLinks: string[];
  leadCharacterGender: string;
  targetAudienceGender: string;
  sourceLanguage: string;
  scrapingLink?: string; // Add scraping link from outsourced show
}

export default function InternalDetails() {
  const navigate = useNavigate();
  const { showId } = useParams<{ showId: string }>();
  const { isAuth, loading: authLoading } = useAuth();
  const [showShareModal, setShowShareModal] = useState(false);
  
  // State for metadata-based internal details
  const [data, setData] = useState<InternalDetailsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [outsourcedShow, setOutsourcedShow] = useState<OutsourcedShow | null>(null);

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuth) {
      navigate('/', { replace: true });
    }
  }, [isAuth, authLoading, navigate]);

  // Fetch internal details metadata when showId is available
  useEffect(() => {
    const fetchInternalDetailsMetadata = async () => {
      if (!showId || !isAuth || authLoading) return;

      setLoading(true);
      setError(null);

      try {
        // First, fetch the full show to get outsourcedShowId
        const mainShow = await fetchShow(showId, {
          requestedMetadata: ['SUCCESSFUL_PROMOS'],
          includeEpisodes: false,
          includeEpisodeUserMapping: false
        });

        const metadataTypes = [
          ShowMetaDataType.ACTOR_ETHNICITY,
          ShowMetaDataType.LEAD_CHARACTER_GENDER,
          ShowMetaDataType.TARGET_AUDIENCE_GENDER,
          ShowMetaDataType.SOURCE_LANGUAGE,
          ShowMetaDataType.SOURCE_SUBTITLE_LANGUAGE,
          ShowMetaDataType.TRAILER_LINKS,
          ShowMetaDataType.PROMO_PERFORMANCE_DETAILS,
          ShowMetaDataType.SUCCESSFUL_PROMOS
        ];

        const showData = await fetchShowMetadata(showId, metadataTypes);

        // Extract metadata values and map to InternalDetailsData format
        const metadata = showData.metadata || [];
        let internalDetailsData: InternalDetailsData = {
          actorEthnicity: metadata.find(m => m.type === ShowMetaDataType.ACTOR_ETHNICITY)?.value || '',
          leadCharacterGender: metadata.find(m => m.type === ShowMetaDataType.LEAD_CHARACTER_GENDER)?.value || '',
          targetAudienceGender: metadata.find(m => m.type === ShowMetaDataType.TARGET_AUDIENCE_GENDER)?.value || '',
          sourceLanguage: metadata.find(m => m.type === ShowMetaDataType.SOURCE_LANGUAGE)?.value || '',
          sourceSubtitleLanguage: metadata.find(m => m.type === ShowMetaDataType.SOURCE_SUBTITLE_LANGUAGE)?.value || '',
          trailerLinks: metadata.find(m => m.type === ShowMetaDataType.TRAILER_LINKS)?.value?.split(',') || [],
        };

        if (mainShow.outsourcedShowId) {
          try {
            const outsourcedShowData = await outsourcedShowService.getOutsourcedShowById(mainShow.outsourcedShowId);
            setOutsourcedShow(outsourcedShowData);
            // Add scraping link to internal details data
            internalDetailsData.scrapingLink = outsourcedShowData.scrapingLink || '';
          } catch (outsourcedError) {
            console.error('Error fetching outsourced show details:', outsourcedError);
            // Continue without outsourced show data
          }
        }

        setData(internalDetailsData);
        console.log('Internal details metadata loaded:', internalDetailsData);

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch internal details';
        setError(errorMessage);
        console.error('Error fetching internal details metadata:', err);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchInternalDetailsMetadata();
  }, [showId, isAuth, authLoading]);

  // Auto-save with debounce when data changes
  useEffect(() => {
    if (!hasUnsavedChanges || !data || !showId) return;
    
    const timeoutId = setTimeout(async () => {
      try {
        const metadataList = [
          { type: ShowMetaDataType.ACTOR_ETHNICITY, value: data.actorEthnicity },
          { type: ShowMetaDataType.LEAD_CHARACTER_GENDER, value: data.leadCharacterGender },
          { type: ShowMetaDataType.TARGET_AUDIENCE_GENDER, value: data.targetAudienceGender },
          { type: ShowMetaDataType.SOURCE_LANGUAGE, value: data.sourceLanguage },
          { type: ShowMetaDataType.SOURCE_SUBTITLE_LANGUAGE, value: data.sourceSubtitleLanguage },
          { type: ShowMetaDataType.TRAILER_LINKS, value: data.trailerLinks.join(',') }
        ].filter(metadata => metadata.value);

        await updateShowMetadata(showId, metadataList);
        setHasUnsavedChanges(false);
        console.log('Internal details auto-saved');
      } catch (err) {
        console.error('Auto-save failed:', err);
        // Don't show error toast for auto-save failures to avoid being intrusive
      }
    }, 2000); // 2-second debounce

    return () => clearTimeout(timeoutId);
  }, [data, hasUnsavedChanges, showId]);

  // Show loading state while checking authentication
  if (authLoading || loading) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">
            {authLoading ? 'Authenticating...' : 'Loading internal details...'}
          </p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuth) {
    return null;
  }

  // Handle case where showId is missing
  if (!showId) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Show ID is required</p>
          <Button onClick={() => navigate('/publish')} className="mt-4">
            Back to Publish
          </Button>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    if (!data || !showId) {
      toast.error('No data to save');
      return;
    }

    setLoading(true);
    try {
      // Convert InternalDetailsData to ShowMetaDataDTO format
      const metadataList = [
        { type: ShowMetaDataType.ACTOR_ETHNICITY, value: data.actorEthnicity },
        { type: ShowMetaDataType.LEAD_CHARACTER_GENDER, value: data.leadCharacterGender },
        { type: ShowMetaDataType.TARGET_AUDIENCE_GENDER, value: data.targetAudienceGender },
        { type: ShowMetaDataType.SOURCE_LANGUAGE, value: data.sourceLanguage },
        { type: ShowMetaDataType.SOURCE_SUBTITLE_LANGUAGE, value: data.sourceSubtitleLanguage },
        { type: ShowMetaDataType.TRAILER_LINKS, value: data.trailerLinks.join(',') }
      ].filter(metadata => metadata.value); // Only include non-empty values

      await updateShowMetadata(showId, metadataList);
      setHasUnsavedChanges(false);
      toast.success('Internal details saved successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save internal details';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDataChange = (newData: InternalDetailsData) => {
    setData(newData);
    setHasUnsavedChanges(true);
  };

  const handleContinue = async () => {
    // Auto-save if there are unsaved changes
    if (hasUnsavedChanges && data && showId) {
      try {
        toast.loading('Saving changes...', { id: 'auto-save' });
        const metadataList = [
          { type: ShowMetaDataType.ACTOR_ETHNICITY, value: data.actorEthnicity },
          { type: ShowMetaDataType.LEAD_CHARACTER_GENDER, value: data.leadCharacterGender },
          { type: ShowMetaDataType.TARGET_AUDIENCE_GENDER, value: data.targetAudienceGender },
          { type: ShowMetaDataType.SOURCE_LANGUAGE, value: data.sourceLanguage },
          { type: ShowMetaDataType.SOURCE_SUBTITLE_LANGUAGE, value: data.sourceSubtitleLanguage },
          { type: ShowMetaDataType.TRAILER_LINKS, value: data.trailerLinks.join(',') }
        ].filter(metadata => metadata.value);

        await updateShowMetadata(showId, metadataList);
        setHasUnsavedChanges(false);
        toast.success('Changes saved successfully!', { id: 'auto-save' });
      } catch (error) {
        toast.error('Failed to save changes', { id: 'auto-save' });
        return; // Don't navigate if save failed
      }
    }
    navigate(`/publish/${showId}/thumbnails`);
  };

  const handleBack = () => {
    navigate(`/publish/${showId}`);
  };

  return (
    <PublishLayout onSave={handleSave} onContinue={handleContinue}>
      <div className={styles.contentWrapper}>
          {/* Page Title */}
          <div className={styles.pageHeader}>
            <h2 className={styles.pageTitle}>Internal Details</h2>
            <div className={styles.headerActions}>
              <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)}>
                <Users className="h-4 w-4 mr-2" />
                Invite
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className={styles.iconButton}
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                }}
              >
                <Link2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className={styles.sectionSpacing}>
            {error && (
              <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-4 mb-6">
                <p className="text-sm font-medium">Error loading data:</p>
                <p className="text-sm">{error}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </Button>
              </div>
            )}
            
            <InternalDetailsForm
              data={data || {
                actorEthnicity: '',
                sourceSubtitleLanguage: '',
                trailerLinks: [],
                leadCharacterGender: '',
                targetAudienceGender: '',
                sourceLanguage: '',
                scrapingLink: ''
              }}
              onChange={handleDataChange}
            />
            
            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-8">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleContinue}
                className="flex items-center gap-2"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      <ShareModal open={showShareModal} onOpenChange={setShowShareModal} />
    </PublishLayout>
  );
}
